import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Sparkles, X, CornerDownLeft, AlertTriangle } from 'lucide-react';
import { aiApi, type SearchQueryResult } from '../../api/ai';

interface Props {
  /** Set when a question is active — drives the results list. */
  result: SearchQueryResult | null;
  onResult: (r: SearchQueryResult | null) => void;
  /** Matching ticket count, from the list query the filters drive. */
  total?: number;
  /** True while that list query is in flight. */
  loading?: boolean;
}

/** Questions whose answer is a number, not a list. */
const COUNT_RE = /\b(how many|how much|count|number of|total)\b/i;

const EXAMPLES = [
  'critical tickets still open',
  'how did we fix the printer at the clinic',
  'my waiting-on-customer tickets from last month',
];

/**
 * Natural-language search over tickets. The question is translated into the
 * same filters the manual controls produce — the answer is a ticket list, not
 * generated prose, and the chips show exactly how the question was read so a
 * wrong interpretation is visible rather than silently wrong.
 */
export default function AskSearch({ result, onResult, total, loading }: Props) {
  const [question, setQuestion] = useState('');
  /** The question the current result belongs to, for phrasing the answer. */
  const [asked, setAsked] = useState('');

  const ask = useMutation({
    mutationFn: (q: string) => aiApi.searchQuery(q),
    onSuccess: (r, q) => { setAsked(q); onResult(r); },
    onError: (e: Error) => toast.error(e.message || 'Could not interpret that question'),
  });

  const submit = () => {
    const q = question.trim();
    if (q) ask.mutate(q);
  };

  const clear = () => {
    setQuestion('');
    setAsked('');
    onResult(null);
  };

  // "How many…" deserves a number, not a list to count by hand. The list query
  // already returns the total, so answer with it directly.
  const isCount = COUNT_RE.test(asked);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[16rem]">
          <Sparkles className="w-4 h-4 text-primary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            className="input w-full"
            style={{ paddingLeft: '2.25rem', paddingRight: '2rem' }}
            placeholder="Ask a question about your tickets…"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submit(); }
              if (e.key === 'Escape' && result) clear();
            }}
          />
          {question && (
            <button
              type="button"
              onClick={clear}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-fg"
              aria-label="Clear question"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={!question.trim() || ask.isPending}
        >
          {ask.isPending ? 'Thinking…' : <><CornerDownLeft className="w-3.5 h-3.5" /> Ask</>}
        </button>
      </div>

      {/* Examples, until the user has asked something. */}
      {!result && !ask.isPending && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-muted">Try:</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => { setQuestion(ex); ask.mutate(ex); }}
              className="text-[11px] px-2 py-0.5 rounded-full border border-line text-muted hover:text-fg hover:border-primary transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {/* How the question was read — visible so a misreading is correctable. */}
      {result && (
        <div className="rounded-lg border border-line bg-row-odd px-3 py-2 space-y-1.5">
          {/* The answer. Always shown: even for "show me…" the count is the
              first thing worth knowing, and for "how many…" it IS the answer. */}
          <div className="flex items-baseline gap-2 flex-wrap">
            {loading || total === undefined ? (
              <span className="text-sm text-muted">Counting…</span>
            ) : (
              <>
                <span
                  className={isCount ? 'text-2xl font-bold tabular-nums' : 'text-base font-bold tabular-nums'}
                  style={{ color: 'var(--scpa-primary)' }}
                >
                  {total}
                </span>
                <span className="text-sm text-fg">
                  {total === 1 ? 'ticket matches' : 'tickets match'}
                  {isCount ? '' : ' — listed below'}
                </span>
              </>
            )}
          </div>

          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted shrink-0 mt-0.5">
              Searching for
            </span>
            {result.applied.length === 0 ? (
              <span className="text-xs text-muted">everything</span>
            ) : (
              result.applied.map((a) => (
                <span
                  key={`${a.label}:${a.value}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-primary-muted text-primary"
                >
                  <span className="opacity-70">{a.label}</span> {a.value}
                </span>
              ))
            )}
            <button
              type="button"
              onClick={clear}
              className="ml-auto text-[11px] text-muted hover:text-err shrink-0"
            >
              Clear
            </button>
          </div>

          {result.explanation && (
            <p className="text-[11px] text-muted italic">{result.explanation}</p>
          )}

          {result.unmapped.length > 0 && (
            <p className="text-[11px] flex items-start gap-1.5" style={{ color: 'var(--scpa-status-warning)' }}>
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              <span>
                Ignored (nothing in this workspace matches): {result.unmapped.join(', ')}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
